import { call, put, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import toast from 'react-hot-toast';
import axios from 'axios';
import client from '../../service/graphql';
import {
  getCoachPublicDetailsFailure,
  getCoachPublicDetailsRequest,
  getCoachPublicDetailsSuccess,
  createCoachMiniwebBookingRequest,
  createCoachMiniwebBookingSuccess,
  createCoachMiniwebBookingFailure,
  ICoachMiniWebBookingInput,
  getAvailableTimesRequest,
  getAvailableTimesRequestSuccess,
  getAvailableTimesRequestFailure,
  fetchCoachAvailabilitiesForMiniweb,
  fetchCoachAvailabilitiesForMiniwebFailure,
  fetchCoachAvailabilitiesForMiniwebSuccess,
  IGetBookingsForSelectedMonth,
  fetchBookingsByMonthForMiniweb,
  fetchBookingsByMonthForMiniwebSuccess,
  fetchBookingsByMonthForMiniwebFailure,
} from './miniwebSlice';
import { toastStyles } from '../../utils/utils';
import {
  CREATE_COACH_MINIWEB_BOOKING,
  GET_COACH_PUBLIC_DETAILS,
  GET_MINIWEB_COACH_AVAILABILITIES,
  GET_MINIWEB_BOOKINGS_FOR_MONTH,
  UPLOAD_REFERENCES_IN_MINIWEB_BOOKING,
  GET_AVAILABLE_TIMES,
} from '../../service/graphql/miniwebQuery';

async function saveprofilePicInS3({
  fileObject,
  preSignedUrl,
}: {
  fileObject: File | null;
  preSignedUrl: string;
}): Promise<void> {
  if (fileObject?.name) {
    await axios.put(preSignedUrl, fileObject, {
      headers: {
        'Content-Type': `${fileObject.type}`,
      },
    });
  }
}

function* watchGetCoachPublicDetails({ payload }: PayloadAction<{ username: string }>) {
  try {
    const result = yield call(client.query.bind(client), {
      query: GET_COACH_PUBLIC_DETAILS,
      variables: { username: payload.username },
    });
    yield put(getCoachPublicDetailsSuccess(result.data.getCoachPublicDetails));
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(getCoachPublicDetailsFailure(error.message));
    }
  }
}

function* watchCreateCoachMiniwebBooking({ payload }: PayloadAction<ICoachMiniWebBookingInput>) {
  try {
    const { coachMiniWebBooking, referenceFile } = payload;
    if (referenceFile) {
      const result = yield call(client.mutate.bind(client), {
        mutation: UPLOAD_REFERENCES_IN_MINIWEB_BOOKING,
        variables: {
          clientEmail: coachMiniWebBooking.clientEmail,
          username: coachMiniWebBooking.username,
          fileName: referenceFile[0].name,
          contentType: referenceFile[0].type,
        },
      });
      const payloadObj = {
        fileObject: referenceFile[0],
        preSignedUrl: result.data.uploadClientReferenceFile.url,
      };
      yield call(saveprofilePicInS3, payloadObj);
      coachMiniWebBooking.referenceText = result.data.uploadClientReferenceFile.key;
    }
    const result = yield call(client.mutate.bind(client), {
      mutation: CREATE_COACH_MINIWEB_BOOKING,
      variables: { coachMiniWebBooking: payload.coachMiniWebBooking },
    });
    yield put(createCoachMiniwebBookingSuccess(result.data.createBookingInCoachMiniWeb));
    toast.success('Booking created successfully', toastStyles.success);
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(createCoachMiniwebBookingFailure(error.message));
      toast.error(error.message, toastStyles.error);
    }
  }
}

function* watchGetAvailableTimesRequest({
  payload,
}: PayloadAction<{
  username: string;
  date: number;
  month: number;
  year: number;
  duration: number;
}>) {
  try {
    const result = yield call(client.query.bind(client), {
      query: GET_AVAILABLE_TIMES,
      variables: {
        username: payload.username,
        date: payload.date,
        month: payload.month,
        year: payload.year,
        duration: payload.duration,
      },
    });
    yield put(getAvailableTimesRequestSuccess(result.data.getAvailableTimes));
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(getAvailableTimesRequestFailure(error.message));
    }
  }
}

function* watchFetchCoachAvailabilities({ payload }: PayloadAction<{ username: string }>) {
  try {
    const result = yield call(client.query.bind(client), {
      query: GET_MINIWEB_COACH_AVAILABILITIES,
      variables: { username: payload.username },
    });
    yield put(
      fetchCoachAvailabilitiesForMiniwebSuccess(result.data.findCoachAvailabilitiesForMiniweb)
    );
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(fetchCoachAvailabilitiesForMiniwebFailure());
    }
  }
}

function* watchFetchBookingsByMonth({ payload }: PayloadAction<IGetBookingsForSelectedMonth>) {
  const { username, month, year } = payload;
  try {
    const response = yield client.query({
      query: GET_MINIWEB_BOOKINGS_FOR_MONTH,
      variables: { username, month, year },
    });
    yield put(
      fetchBookingsByMonthForMiniwebSuccess(response.data.getBookedDatesPerMonthForMiniweb)
    );
  } catch (error) {
    if (error instanceof Error) {
      yield put(fetchBookingsByMonthForMiniwebFailure());
    }
  }
}

export function* watchMiniwebSagas() {
  yield takeLatest(getCoachPublicDetailsRequest, watchGetCoachPublicDetails);
  yield takeLatest(createCoachMiniwebBookingRequest, watchCreateCoachMiniwebBooking);
  yield takeLatest(getAvailableTimesRequest, watchGetAvailableTimesRequest);
  yield takeLatest(fetchCoachAvailabilitiesForMiniweb, watchFetchCoachAvailabilities);
  yield takeLatest(fetchBookingsByMonthForMiniweb, watchFetchBookingsByMonth);
}

const miniwebSagas = watchMiniwebSagas;
export default miniwebSagas;
